/**
 * Presentational primitives.
 *
 * Thin wrappers over the component classes declared in index.css: the styling
 * decisions live in CSS so they can be re-themed, and these components only add
 * semantics and accessibility.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType, HTMLAttributes, KeyboardEvent, ReactNode } from "react";

import { cn, toTitleCase } from "@/lib/format";
import { toneClasses, verdictClass, type Domain, type Tone, toneFor } from "@/lib/tone";

export function Panel({
  children,
  className,
  flush,
  ...rest
}: {
  children: ReactNode;
  className?: string;
  flush?: boolean;
  /** Anything else a `div` takes — `role`, `id`, `aria-labelledby` for a tab panel. */
} & Omit<HTMLAttributes<HTMLDivElement>, "className" | "children">) {
  return (
    <div className={cn(flush ? "panel-flush" : "panel", className)} {...rest}>
      {children}
    </div>
  );
}

export function PanelHeader({
  title,
  subtitle,
  actions,
  icon,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
      <div className="flex items-start gap-2.5 min-w-0">
        {icon ? <span className="mt-0.5 text-muted-foreground">{icon}</span> : null}
        <div className="min-w-0">
          <h2 className="text-sm font-semibold truncate">{toTitleCase(title)}</h2>
          {subtitle ? (
            <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
    </div>
  );
}

export function StatusBadge({
  domain,
  status,
  label,
  dot = true,
}: {
  domain: Domain;
  status: string | null | undefined;
  label?: string;
  dot?: boolean;
}) {
  const tone: Tone = toneFor(domain, status);
  return (
    <span className={cn("badge", dot && "badge-dot", toneClasses(tone))}>
      {toTitleCase(label ?? (status ?? "").replace(/_/g, " "))}
    </span>
  );
}

export function Verdict({
  evaluation,
  label,
}: {
  evaluation: string | null | undefined;
  label?: string;
}) {
  const text =
    label ??
    (evaluation === "conforms"
      ? "Conforms"
      : evaluation === "not_conforms"
        ? "Does not conform"
        : evaluation === "inconclusive"
          ? "Inconclusive"
          : evaluation === "not_applicable"
            ? "Not applicable"
            : "Pending");
  return <span className={cn("verdict", verdictClass(evaluation))}>{toTitleCase(text)}</span>;
}

export function Metric({
  label,
  value,
  hint,
  tone,
  icon,
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  icon?: ReactNode;
  onClick?: () => void;
}) {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      className={cn("metric text-left w-full", onClick && "cursor-pointer")}
      onClick={onClick}
      type={onClick ? "button" : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="metric-label">{toTitleCase(label)}</span>
        {icon ? <span className="text-muted-foreground">{icon}</span> : null}
      </div>
      <div className={cn("metric-value mt-1.5", tone === "overdue" && "text-status-overdue")}>
        {value}
      </div>
      {hint ? <div className="text-xs text-muted-foreground mt-1">{hint}</div> : null}
    </Component>
  );
}

export function EmptyState({
  title,
  description,
  icon,
  action,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon ? <div className="text-muted-foreground/60">{icon}</div> : null}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description ? <p className="text-xs max-w-sm">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton", className ?? "h-4 w-full")} />;
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-3">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn("h-8", columnIndex === 0 ? "w-40" : "flex-1")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block", className)}>
      <span className="block text-xs font-semibold mb-1.5">
        {toTitleCase(label)}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </span>
      {children}
      {error ? (
        <span className="block text-xs text-destructive mt-1">{error}</span>
      ) : hint ? (
        <span className="block text-xs text-muted-foreground mt-1">{hint}</span>
      ) : null}
    </label>
  );
}

export function KeyValue({
  items,
  columns = 2,
}: {
  items: { label: string; value: ReactNode }[];
  columns?: 1 | 2 | 3;
}) {
  return (
    <dl
      className={cn(
        "grid gap-x-6 gap-y-2.5 text-sm",
        columns === 1 ? "grid-cols-1" : columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3",
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{toTitleCase(item.label)}</dt>
          <dd className="font-medium truncate">{item.value ?? ""}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ProgressBar({ percent, tone }: { percent: number; tone?: Tone }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden" title={`${clamped}%`}>
      <div
        className={cn(
          "h-full rounded-full transition-all duration-500",
          tone === "overdue" ? "bg-status-overdue" : "bg-brand",
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div className="min-w-0">
        {breadcrumb ? (
          <div className="text-xs text-muted-foreground mb-1">{breadcrumb}</div>
        ) : null}
        <h1 className="text-xl font-semibold tracking-tight truncate">{toTitleCase(title)}</h1>
        {subtitle ? <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/**
 * A row of registers, and one line saying what the chosen one is.
 *
 * The description sits *under* the strip rather than inside each tab. Putting a
 * sentence in every tab makes five sentences compete on one line, and forces every
 * tab to the width of the longest one rather than the width of its own name — so
 * the strip stops being scannable at exactly the point it has enough tabs to need
 * to be. Only the register you are looking at needs its sentence, and there it has
 * room to be one.
 *
 * A real `tablist`, not a row of buttons: arrow keys move between tabs, Home and End
 * jump to the ends, and only the selected tab is in the tab order. That is what a
 * keyboard user expects the moment something calls itself a tab, and it is the
 * difference between this and a segmented control that merely looks like one.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label = "Registers",
}: {
  tabs: {
    id: T;
    label: string;
    hint?: string;
    icon?: ComponentType<{ size?: number; className?: string }>;
    /** Shown only when known — see the note on `.tab-count`. */
    count?: number | null;
  }[];
  value: T;
  onChange: (next: T) => void;
  label?: string;
}) {
  const active = tabs.find((tab) => tab.id === value);
  const strip = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState<"none" | "start" | "end" | "both">("none");

  /** Which sides have more tabs off-screen, so the fade can say so. */
  const measure = useCallback(() => {
    const node = strip.current;
    if (!node) return;
    const overflowing = node.scrollWidth - node.clientWidth > 1;
    if (!overflowing) return setEdges("none");
    const atStart = node.scrollLeft <= 1;
    const atEnd = node.scrollLeft + node.clientWidth >= node.scrollWidth - 1;
    setEdges(atStart ? "end" : atEnd ? "start" : "both");
  }, []);

  useEffect(() => {
    const node = strip.current;
    if (!node) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure, tabs.length]);

  // On a narrow screen the selected tab can start out beyond the right edge, which
  // would leave the strip looking as though nothing were selected at all.
  useEffect(() => {
    strip.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
    measure();
  }, [value, measure]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const index = tabs.findIndex((tab) => tab.id === value);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? tabs.length - 1
          : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
    onChange(tabs[next]!.id);
  }

  return (
    <div className="mb-4">
      <div
        ref={strip}
        className="tabs"
        role="tablist"
        aria-label={label}
        data-edges={edges}
        onKeyDown={onKeyDown}
        onScroll={measure}
      >
        {tabs.map((tab) => {
          const selected = tab.id === value;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className="tab"
              onClick={() => onChange(tab.id)}
            >
              {Icon ? <Icon size={14} className="tab-icon" /> : null}
              {toTitleCase(tab.label)}
              {typeof tab.count === "number" ? (
                <span className="tab-count">{tab.count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      {active?.hint ? <p className="tab-hint">{active.hint}</p> : null}
    </div>
  );
}
